"use client";

import { DailyLog, UserProfile } from "@/types";
import { useState, useEffect, useMemo } from "react";
import { getLast7DaysISO } from "@/utils/date";
import { Activity, Sparkles, Target, Trophy } from "lucide-react";
import clsx from "clsx";
import { calculateDailyScore, calculateLifestyleScore, calculateLifestyleBreakdown, defaultProfile, calculateLongevityAge } from "@/utils/scoring";
import { fetchAICoachAnalysis } from "@/utils/storage";
import LongevityCard from "./LongevityCard";

interface WeekViewProps {
    logs: DailyLog[];
    profile?: Partial<UserProfile>;
}

interface WeeklyAnalysis {
    weekly_score: number;
    summary: string;
    wins: string[];
    opportunities: string[];
    action_plan: string[];
    coach_tip: string;
    biological_age_estimate?: number;
}

const getSleepHours = (sleep: number | { hours: number; quality?: number } | undefined) => {
    if (sleep === undefined) return 0;
    return typeof sleep === 'object' ? sleep.hours : sleep;
};

export default function WeekView({ logs, profile = defaultProfile }: WeekViewProps) {
    // Generate last 7 days for consistent history
    const [last7Days] = useState<string[]>(() => getLast7DaysISO());
    const [analysis, setAnalysis] = useState<WeeklyAnalysis | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    // Process Data for Last 7 Days
    const dailyData = last7Days.map(date => {
        const log = logs.find(l => l.date === date);
        if (!log) return { date, status: "missing", score: 0, lifestyle: 0 };

        return {
            date,
            status: log.workouts?.some(w => w.type !== "Rest") ? "done" : "rest",
            score: calculateDailyScore(log),
            lifestyle: calculateLifestyleScore(log)
        };
    });

    const activeLogs = useMemo(() => logs.filter(l => last7Days.includes(l.date)), [logs, last7Days]);
    const totalLogs = activeLogs.length;

    // Averages
    const avgScore = totalLogs > 0 ? Math.round(dailyData.reduce((sum: number, d) => sum + d.score, 0) / totalLogs) : 0;
    const avgLifestyle = totalLogs > 0 ? (dailyData.reduce((sum: number, d) => sum + d.lifestyle, 0) / totalLogs).toFixed(1) : "0.0";

    // Detailed Averages
    const avgProtein = totalLogs > 0
        ? Math.round(activeLogs.reduce((sum: number, log) => sum + (log.meals || []).reduce((s: number, m) => s + (m.items || []).reduce((ss: number, i) => ss + (i.protein || 0) * (i.quantity || 1), 0), 0), 0) / totalLogs)
        : 0;
    const avgSleep = totalLogs > 0
        ? (activeLogs.reduce((sum: number, log) => sum + getSleepHours(log.sleep), 0) / totalLogs).toFixed(1)
        : "0.0";
    const avgSteps = totalLogs > 0
        ? Math.round(activeLogs.reduce((sum: number, log) => sum + (log.steps || 0), 0) / totalLogs)
        : 0;


    // Lifestyle Analysis Logic
    const lifestyleBreakdowns = dailyData.map(d => {
        const log = activeLogs.find(l => l.date === d.date);
        return log ? calculateLifestyleBreakdown(log) : { alcohol: 0, smoking: 0, stress: 0, circadian: 0, social: 0, total: 0 };
    });

    const avgAlcScore = totalLogs > 0 ? (lifestyleBreakdowns.reduce((sum, b) => sum + b.alcohol, 0) / totalLogs) : 0;
    const avgSmkScore = totalLogs > 0 ? (lifestyleBreakdowns.reduce((sum, b) => sum + b.smoking, 0) / totalLogs) : 0;
    const avgStrScore = totalLogs > 0 ? (lifestyleBreakdowns.reduce((sum, b) => sum + b.stress, 0) / totalLogs) : 0;
    const avgCirScore = totalLogs > 0 ? (lifestyleBreakdowns.reduce((sum, b) => sum + b.circadian, 0) / totalLogs) : 0;
    const avgSocScore = totalLogs > 0 ? (lifestyleBreakdowns.reduce((sum, b) => sum + b.social, 0) / totalLogs) : 0;

    const totalAlcohol = activeLogs.reduce((sum: number, log) => sum + (log.alcohol?.units || 0), 0);
    const totalCigs = activeLogs.reduce((sum: number, log) => sum + (log.smoking?.quantity || 0), 0);

    // Formatting Helpers
    const formatScore = (val: number) => (val > 0 ? `+${val.toFixed(1)}` : val.toFixed(1));
    const getBarWidth = (val: number, min: number, max: number) => {
        const percent = Math.max(0, Math.min(100, ((val - min) / (max - min)) * 100));
        return `${percent}%`;
    };

    // Fetch AI Analysis
    useEffect(() => {
        if (totalLogs === 0) return;

        const fetchAnalysis = async () => {
            setIsLoading(true);
            try {
                const data = await fetchAICoachAnalysis('weekly', profile as UserProfile, { logs: activeLogs });
                setAnalysis(data);
            } catch (error) {
                console.error("Failed to fetch weekly analysis", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchAnalysis();
    }, [totalLogs, profile, activeLogs]);

    if (last7Days.length === 0) return null;

    // Summary Analysis Values
    const avgCoreScore = totalLogs > 0 ? (activeLogs.reduce((sum, log) => sum + (calculateDailyScore(log) - calculateLifestyleScore(log)), 0) / totalLogs) : 0;

    // Day Counts
    const highDays = dailyData.filter(d => d.score >= 80).length;
    const goodDays = dailyData.filter(d => d.score >= 60 && d.score < 80).length;
    const recoveryDays = dailyData.filter(d => d.score < 60).length;

    // Metric targets
    const proteinTargetDays = activeLogs.filter(log => {
        const totalP = (log.meals || []).reduce((sum, m) => sum + (m.items || []).reduce((s, i) => s + (i.protein || 0) * (i.quantity || 1), 0), 0);
        const weight = profile.weight || 70;
        return totalP >= (1.6 * weight);
    }).length;

    const sleepDips = activeLogs.filter(log => getSleepHours(log.sleep) < 6.5).length;

    const WORKOUT_OPTIONS = {
        "Strength": ["Chest workout", "Back workout", "Shoulders", "Arms (biceps + triceps)", "Legs – Strength Day", "Glutes workout", "Full-body strength", "Abs/Core workout", "Calisthenics", "Machine strength"],
        "Cardio": ["Running – Light Jog", "Running – Moderate", "Running – Intense", "Walking – Normal", "Walking – Fast", "Cycling – Outdoor", "Cycling – Indoor", "HIIT workout", "Rowing machine", "Stairmaster", "Elliptical", "Swimming", "Jump rope"],
        "Mobility": ["Yoga – Hatha", "Yoga – Vinyasa", "Stretching", "Pilates", "Foam rolling", "Breathwork", "Meditation"],
        "Sports": ["Badminton – Casual", "Badminton – Intense", "Tennis – Casual", "Tennis – Intense", "Football", "Basketball", "Cricket – Casual", "Cricket – Match", "Hiking"]
    };

    // Workout Splits
    const isStrength = (type: string) => WORKOUT_OPTIONS["Strength"].includes(type);
    const isCardio = (type: string) => WORKOUT_OPTIONS["Cardio"].includes(type) || WORKOUT_OPTIONS["Sports"].includes(type);

    const strengthCount = activeLogs.filter(log => log.workouts?.some(w => w.type && isStrength(w.type))).length;
    const cardioCount = activeLogs.filter(log => log.workouts?.some(w => w.type && isCardio(w.type))).length;
    const restCount = activeLogs.filter(log => !log.workouts || log.workouts.every(w => !w.type || w.type === "Rest" || w.type === "Mobility" || WORKOUT_OPTIONS["Mobility"].includes(w.type))).length;


    // Calculate Average Longevity Age (Local Fallback)
    const localLongevityAge = totalLogs > 0
        ? (activeLogs.reduce((sum, log) => sum + calculateLongevityAge(log, profile), 0) / totalLogs)
        : calculateLongevityAge(null, profile);

    // Use AI analysis if available, otherwise local calculation
    const displayLongevityAge = analysis?.biological_age_estimate || localLongevityAge;

    return (
        <div className="flex flex-col gap-6 pb-24">
            {/* Average Longevity Card */}
            {/* Average Longevity Card with Loading State */}
            {totalLogs > 0 && (
                <div className="relative">
                    {isLoading ? (
                        <div className="glass-card flex flex-col items-center justify-center h-[140px] animate-pulse relative overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full animate-[shimmer_1.5s_infinite]" />
                            <Sparkles className="text-emerald-400 mb-3 animate-spin duration-[3000ms]" size={24} />
                            <div className="text-sm font-bold text-emerald-400 uppercase tracking-widest">Updating Longevity Age</div>
                            <div className="text-[10px] text-text-muted mt-1">Analyzing weekly biomarkers...</div>
                        </div>
                    ) : (
                        <LongevityCard
                            chronologicalAge={profile?.age || 30}
                            longevityAge={parseFloat(displayLongevityAge.toFixed(1))}
                            weight={profile?.weight || 70}
                            height={profile?.height || 175}
                            longevityScore={avgScore}
                        />
                    )}
                </div>
            )}

            {/* Top Score Graph Card */}
            <div className="glass-card">
                <div className="flex justify-between items-end mb-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-text-muted font-bold">Weekly Performance</div>
                        <div className="text-2xl font-bold text-white mt-1">{avgScore}<span className="text-sm font-normal text-text-muted">/100 avg</span></div>
                    </div>
                </div>

                <div className="h-[120px] rounded-xl bg-black/20 border border-white/5 relative overflow-hidden mb-2">
                    <div className="absolute inset-0 opacity-20 bg-[linear-gradient(to_right,rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(to_top,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[size:12px_12px]"></div>

                    {/* Padded wrapper to prevent label clipping */}
                    <div className="absolute inset-0 px-3 py-6">
                        <div className="relative w-full h-full">
                            <svg className="absolute inset-0 w-full h-full overflow-visible" preserveAspectRatio="none" viewBox="0 0 100 100">
                                <defs>
                                    <linearGradient id="graphGradient" x1="0" x2="0" y1="0" y2="1">
                                        <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.5" />
                                        <stop offset="100%" stopColor="var(--primary)" stopOpacity="0" />
                                    </linearGradient>
                                </defs>
                                {(() => {
                                    const points = dailyData.map((day, i) => {
                                        // Use real score (0-100), fallback to 10 for visibility if 0
                                        const value = Math.max(10, day.score);
                                        const x = (i / (dailyData.length - 1)) * 100;
                                        const y = 100 - value;
                                        return `${x},${y}`;
                                    }).join(' ');

                                    const firstY = 100 - Math.max(10, dailyData[0].score);
                                    const lastY = 100 - Math.max(10, dailyData[dailyData.length - 1].score);

                                    return (
                                        <>
                                            <path
                                                d={`M0,100 L0,${firstY} ${points.split(' ').map(p => 'L' + p).join(' ')} L100,${lastY} L100,100 Z`}
                                                fill="url(#graphGradient)"
                                            />
                                            <path
                                                d={`M0,${firstY} ${points.split(' ').map(p => 'L' + p).join(' ')}`}
                                                fill="none"
                                                stroke="var(--primary)"
                                                strokeWidth="2"
                                                vectorEffect="non-scaling-stroke"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </>
                                    );
                                })()}
                            </svg>

                            {/* HTML Overlay for Scores */}
                            <div className="absolute inset-0 pointer-events-none">
                                {dailyData.map((day, i) => {
                                    if (day.score === 0) return null;

                                    // Align x with the data point
                                    const left = (i / (dailyData.length - 1)) * 100;
                                    // Align y with the data point (100 - score)
                                    const val = Math.max(10, day.score);
                                    const bottom = val;

                                    return (
                                        <div
                                            key={i}
                                            className="absolute transform -translate-x-1/2 -translate-y-[120%] text-[8px] font-bold text-white drop-shadow-md transition-all"
                                            style={{
                                                left: `${left}%`,
                                                bottom: `${bottom}%`
                                            }}
                                        >
                                            {day.score}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex justify-between px-2 mb-4 text-[7px] text-gray-300 font-medium uppercase tracking-wider">
                    {dailyData.map((day, i) => (
                        <span key={i}>{new Date(day.date).toLocaleDateString("en-US", { day: "numeric", timeZone: "UTC" })}</span>
                    ))}
                </div>
            </div>

            {/* Weekly Performance Summary (New Design) */}
            <div className="flex flex-col gap-3">
                <div className="grid grid-cols-2 gap-3">
                    {/* Avg Score */}
                    <div className="glass-card !p-4 flex flex-col justify-between h-[100px]">
                        <div className="text-[10px] uppercase tracking-wider text-text-muted font-bold">Avg Longevity Score</div>
                        <div>
                            <div className="text-2xl font-bold text-white">{avgScore} <span className="text-sm text-text-muted font-normal">/ 100</span></div>
                            <div className="text-[10px] text-gray-400 mt-1 truncate">
                                {highDays} high days • {goodDays} good days • {recoveryDays} recovery
                            </div>
                        </div>
                    </div>

                    {/* Core vs Lifestyle */}
                    <div className="glass-card !p-4 flex flex-col justify-between h-[100px]">
                        <div className="text-[10px] uppercase tracking-wider text-text-muted font-bold">Core vs Lifestyle</div>
                        <div>
                            <div className="flex flex-col">
                                <span className="text-sm font-bold text-white">Core {Math.round(avgCoreScore)}</span>
                                <span className={clsx("text-sm font-bold", parseFloat(avgLifestyle) >= 0 ? "text-emerald-400" : "text-rose-400")}>
                                    Lifestyle {parseFloat(avgLifestyle) >= 0 ? "+" : ""}{avgLifestyle}
                                </span>
                            </div>
                            <div className="text-[10px] text-gray-400 mt-1 truncate">
                                {parseFloat(avgLifestyle) > 2 ? "Clean habits boosted the week" : parseFloat(avgLifestyle) < -2 ? "Lifestyle factors dragged score" : "Balanced lifestyle impact"}
                            </div>
                        </div>
                    </div>

                    {/* Protein Avg */}
                    <div className="glass-card !p-4 flex flex-col justify-between h-[100px]">
                        <div className="text-[10px] uppercase tracking-wider text-text-muted font-bold">Protein Avg</div>
                        <div>
                            <div className="text-xl font-bold text-white">{avgProtein} g <span className="text-sm text-text-muted font-normal">/ day</span></div>
                            <div className="text-[10px] text-gray-400 mt-1 truncate">
                                {proteinTargetDays} days ≥ 1.6 g/kg
                            </div>
                        </div>
                    </div>

                    {/* Sleep & Steps */}
                    <div className="glass-card !p-4 flex flex-col justify-between h-[100px]">
                        <div className="text-[10px] uppercase tracking-wider text-text-muted font-bold">Sleep & Steps</div>
                        <div>
                            <div className="text-xl font-bold text-white">{avgSleep} h • {(avgSteps / 1000).toFixed(1)}k</div>
                            <div className="text-[10px] text-gray-400 mt-1 truncate">
                                {sleepDips > 0 ? `Sleep dipped ${sleepDips} nights` : "Consistent sleep schedule"}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Workout Pills */}
                <div className="flex flex-wrap gap-2">
                    <div className="bg-white/5 border border-white/10 rounded-full px-3 py-1.5 text-[10px] font-medium text-gray-300">
                        {strengthCount} strength days
                    </div>
                    <div className="bg-white/5 border border-white/10 rounded-full px-3 py-1.5 text-[10px] font-medium text-gray-300">
                        {cardioCount} zone 2 / cardio days
                    </div>
                    <div className="bg-white/5 border border-white/10 rounded-full px-3 py-1.5 text-[10px] font-medium text-gray-300">
                        {restCount} lighter / recovery days
                    </div>
                </div>
            </div>

            {/* Lifestyle Trends Analysis (New Design) */}
            <div className="glass-card">
                <div className="flex justify-between items-start mb-6">
                    <div>
                        <h3 className="text-xs font-bold uppercase tracking-widest text-text-muted mb-1">Lifestyle Trends</h3>
                        <p className="text-[10px] text-gray-500">Alcohol, smoking, stress & circadian over the week.</p>
                    </div>
                    <div className={clsx(
                        "text-xs font-bold px-2 py-1 rounded-lg border",
                        parseFloat(avgLifestyle) > 0 ? "text-emerald-300 bg-emerald-500/10 border-emerald-500/20" :
                            parseFloat(avgLifestyle) < 0 ? "text-rose-300 bg-rose-500/10 border-rose-500/20" :
                                "text-gray-300 bg-white/5 border-white/10"
                    )}>
                        {parseFloat(avgLifestyle) > 0 ? "+" : ""}{avgLifestyle} Pts
                    </div>
                </div>

                <div className="space-y-5">
                    {/* Alcohol */}
                    <div>
                        <div className="flex justify-between items-end mb-1.5">
                            <span className="text-xs text-gray-300">Alcohol load (weekly)</span>
                            <span className="text-xs font-medium text-white">{totalAlcohol} drinks • <span className={avgAlcScore < 0 ? "text-rose-400" : "text-emerald-400"}>{formatScore(avgAlcScore)} pts</span></span>
                        </div>
                        <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
                            <div className={clsx("h-full rounded-full", avgAlcScore < 0 ? "bg-gradient-to-r from-rose-600 to-rose-400" : "bg-gradient-to-r from-emerald-600 to-emerald-400")} style={{ width: getBarWidth(avgAlcScore, -5, 5) }} />
                        </div>
                    </div>

                    {/* Smoking */}
                    <div>
                        <div className="flex justify-between items-end mb-1.5">
                            <span className="text-xs text-gray-300">Smoking</span>
                            <span className="text-xs font-medium text-white">{totalCigs > 0 ? `${totalCigs} cigs` : 'Non-smoker'} • <span className={avgSmkScore < 0 ? "text-rose-400" : "text-emerald-400"}>{formatScore(avgSmkScore)} pts</span></span>
                        </div>
                        <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
                            <div className={clsx("h-full rounded-full", avgSmkScore < 0 ? "bg-gradient-to-r from-rose-600 to-rose-400" : "bg-gradient-to-r from-emerald-600 to-emerald-400")} style={{ width: getBarWidth(avgSmkScore, -10, 5) }} />
                        </div>
                    </div>

                    {/* Stress */}
                    <div>
                        <div className="flex justify-between items-end mb-1.5">
                            <span className="text-xs text-gray-300">Stress & recovery</span>
                            <span className={clsx("text-xs font-medium", avgStrScore < 0 ? "text-rose-400" : "text-emerald-400")}>{formatScore(avgStrScore)} avg</span>
                        </div>
                        <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
                            <div className={clsx("h-full rounded-full", avgStrScore < 0 ? "bg-gradient-to-r from-rose-600 to-rose-400" : "bg-gradient-to-r from-emerald-600 to-emerald-400")} style={{ width: getBarWidth(avgStrScore, -3, 5) }} />
                        </div>
                    </div>

                    {/* Circadian */}
                    <div>
                        <div className="flex justify-between items-end mb-1.5">
                            <span className="text-xs text-gray-300">Circadian hygiene</span>
                            <span className={clsx("text-xs font-medium", avgCirScore < 0 ? "text-rose-400" : "text-emerald-400")}>{formatScore(avgCirScore)} avg</span>
                        </div>
                        <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
                            <div className={clsx("h-full rounded-full", avgCirScore < 0 ? "bg-gradient-to-r from-rose-600 to-rose-400" : "bg-gradient-to-r from-emerald-600 to-emerald-400")} style={{ width: getBarWidth(avgCirScore, 0, 3) }} />
                        </div>
                    </div>

                    {/* Social */}
                    <div>
                        <div className="flex justify-between items-end mb-1.5">
                            <span className="text-xs text-gray-300">Social & mood</span>
                            <span className={clsx("text-xs font-medium", avgSocScore < 0 ? "text-rose-400" : "text-emerald-400")}>{formatScore(avgSocScore)} avg</span>
                        </div>
                        <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
                            <div className={clsx("h-full rounded-full", avgSocScore < 0 ? "bg-gradient-to-r from-rose-600 to-rose-400" : "bg-gradient-to-r from-emerald-600 to-emerald-400")} style={{ width: getBarWidth(avgSocScore, 0, 2) }} />
                        </div>
                    </div>
                </div>
            </div>

            {/* AI Coach Analysis */}
            <div className="glass-card">
                <div className="flex items-center gap-2 mb-6">
                    <Sparkles size={16} className="text-emerald-400" />
                    <h2 className="text-xs font-bold uppercase tracking-widest text-gray-400">
                        {isLoading ? "Generating Analysis..." : "Weekly Coach Report"}
                    </h2>
                </div>

                {isLoading && (
                    <div className="h-40 flex flex-col items-center justify-center text-text-muted">
                        <Activity className="animate-pulse mb-2 text-emerald-500" />
                        <span className="text-xs font-medium">Analyzing your week...</span>
                    </div>
                )}

                {!isLoading && analysis && (
                    <div className="space-y-6">
                        {/* Quote */}
                        <div className="border-l-2 border-white pl-4 py-1">
                            <p className="text-sm text-white italic leading-relaxed">
                                &quot;{analysis.coach_tip}&quot;
                            </p>
                        </div>

                        {/* Wins & Focus Grid */}
                        <div className="grid grid-cols-2 gap-4">
                            {/* Top Wins */}
                            <div className="bg-[#1e293b]/50 border border-white/5 rounded-2xl p-4">
                                <div className="flex items-center gap-2 mb-3">
                                    <Trophy size={14} className="text-emerald-400" />
                                    <span className="text-[10px] uppercase font-bold tracking-wider text-emerald-400">Top Wins</span>
                                </div>
                                <ul className="space-y-2">
                                    {analysis.wins?.map((win, i) => (

                                        <li key={i} className="flex items-start gap-2 text-xs text-gray-300">
                                            <span className="mt-1.5 w-1 h-1 rounded-full bg-emerald-400 shrink-0" />
                                            <span className="leading-relaxed">{win}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Focus Area */}
                            <div className="bg-[#1e293b]/50 border border-white/5 rounded-2xl p-4">
                                <div className="flex items-center gap-2 mb-3">
                                    <Target size={14} className="text-amber-400" />
                                    <span className="text-[10px] uppercase font-bold tracking-wider text-amber-400">Focus Area</span>
                                </div>
                                <ul className="space-y-2">
                                    {analysis.opportunities?.map((op, i) => (

                                        <li key={i} className="flex items-start gap-2 text-xs text-gray-300">
                                            <span className="mt-1.5 w-1 h-1 rounded-full bg-amber-400 shrink-0" />
                                            <span className="leading-relaxed">{op}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>

                        {/* Action Plan */}
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-3 font-bold">Next Week&apos;s Plan</div>
                            <div className="space-y-3">
                                {analysis.action_plan?.map((item, i) => (

                                    <div key={i} className="flex items-center gap-4 p-4 rounded-2xl bg-[#1e293b]/50 border border-white/5">
                                        <div className="text-sm font-bold text-emerald-400 w-4 text-center shrink-0">
                                            {i + 1}
                                        </div>
                                        <p className="text-sm text-gray-200 font-medium">{item}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {!isLoading && !analysis && totalLogs === 0 && (
                    <div className="text-center py-12">
                        <div className="w-12 h-12 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-3">
                            <Sparkles size={20} className="text-gray-600" />
                        </div>
                        <p className="text-xs text-gray-500 font-medium">Log your meals and workouts to get AI coaching.</p>
                    </div>
                )}
            </div>
        </div >
    );
}


//comment
